import React, { Component } from 'react';
import {
    Box,
    Container,
    Card,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    withStyles,
    TextField,
} from '@material-ui/core';
import Swal from 'sweetalert2';
import WorkshopLayout from '../components/workshopLayout';
import QuickCompanyModal from '../components/QuickCompanyModal';
import CustomButton from '../../shared/components/common/customButton';
import WorkshopHelpButton from '../workshopHelpModal/workshopHelpModal';
import { withRouter } from 'react-router-dom';
import { WorkshopHomeService } from './service';
import ClientsModal from '../../shared/components/common/clientsModal';
import { LoggedUserService } from '../../../loggedUser/loggedUserService';
import { withSnackbar } from 'notistack';

const WORKSHOP_HOME_STATUSES = WorkshopHomeService.WORKSHOP_HOME_STATUSES;
const normalizeStatus = WorkshopHomeService.normalizeStatus;

function createDefaultCounters() {
    return WORKSHOP_HOME_STATUSES.reduce((acc, status) => {
        acc[status] = 0;
        return acc;
    }, {});
}

function normalizeDigits(value) {
    return String(value || '').replace(/\D/g, '');
}

function normalizeFileNameWithoutExtension(value) {
    const normalizedValue = String(value || '')
        .trim()
        .split(/[\\/]/)
        .pop()
        .toLowerCase();

    return normalizedValue.replace(/\.[^./\\]+$/, '');
}

function getNotaRemessaFileName(item = {}) {
    return item.notaRemessaFileName
        || item.nota_remessa_file_name
        || item.notaRemessaFile
        || item.nota_remessa
        || '';
}

function getFileNamesForSearch(item = {}) {
    const names = [];
    const directLists = [item.serviceFileNames, item.service_file_names];
    const objectLists = [item.serviceFiles, item.service_files];

    directLists.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((name) => {
            if (typeof name === 'string' && name.trim()) {
                names.push(name.trim());
            }
        });
    });

    objectLists.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((file) => {
            if (file && typeof file === 'object') {
                const type = String(file.type || file.fileType || '').toLowerCase();
                if (type !== 'nota_remessa' && type !== 'nota remessa') return;
                const name = file.fileName || file.file_name || file.nome || file.name;
                if (typeof name === 'string' && name.trim()) {
                    names.push(name.trim());
                }
            }
        });
    });

    const fallbackName = getNotaRemessaFileName(item);
    if (fallbackName) {
        names.push(fallbackName);
    }

    return Array.from(new Set(names));
}

function matchesGlobalSearch(item, searchTerm) {
    const query = String(searchTerm || '').trim();
    if (!query) return true;

    const queryDigits = normalizeDigits(query);
    const queryNote = normalizeFileNameWithoutExtension(query);

    const cnpjDigits = normalizeDigits(item?.cnpj);
    const noteNames = getFileNamesForSearch(item)
        .map((name) => normalizeFileNameWithoutExtension(name))
        .filter(Boolean);

    const cnpjMatches = queryDigits.length > 0 && cnpjDigits.includes(queryDigits);
    const notaMatches = queryNote.length > 0 && noteNames.some((name) => name.includes(queryNote));

    return cnpjMatches || notaMatches;
}

const styles = theme => ({
    root: {
        paddingTop: '2rem',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        margin: '0 auto',
        backgroundColor: '#F4F6F8',
        minHeight: '85vh',
    },
    cardsWrapper: {
        display: 'flex',
        gap: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '2rem',
    },
    statusCard: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '16px',
        width: '200px',
        height: '135px',
        boxSizing: 'border-box',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
        }
    },
    statusCounter: {
        fontWeight: 'bold',
        fontSize: '2.5rem',
        color: '#111827',
        lineHeight: 1
    },
    statusBadge: {
        fontSize: '11px',
        lineHeight: 1.2,
        padding: '6px 10px',
        color: '#ffffff',
        fontWeight: '700',
        marginTop: '12px',
        textTransform: 'uppercase',
        borderRadius: '20px',
        wordWrap: 'break-word',
        maxWidth: '100%',
    },
    panelCard: {
        backgroundColor: '#ffffff',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #E5E7EB',
        position: 'relative',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        '&:MuiTypography-root': {
            color: '#111827',
        }
    },
    panelTitle: {
        fontSize: '1.25rem',
        fontWeight: '600',
        color: '#111827',
        marginBottom: '20px',
        textAlign: 'center',
    },
    tableWrapper: {
        overflowX: 'auto',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    },
    tableTransparent: {
        backgroundColor: '#FFFFFF',
        '& .MuiTableCell-root': {
            borderBottom: '1px solid #E5E7EB',
            color: '#374151',
            fontSize: '0.875rem',
            padding: '16px',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: '#F9FAFB',
            color: '#6B7280',
            fontWeight: '600',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '12px 16px',
            borderBottom: '1px solid #E5E7EB',
        },
        '& .MuiTableRow-root:hover': {
            backgroundColor: '#F9FAFB'
        }
    },
    actionLink: {
        fontWeight: '600',
        color: '#2563EB !important',
        backgroundColor: '#EFF6FF',
        padding: '6px 12px',
        borderRadius: '6px',
        textDecoration: 'none',
        cursor: 'pointer',
        display: 'inline-block',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#DBEAFE',
            color: '#1D4ED8 !important',
        }
    },
    panelDescription: {
        marginTop: '.5rem',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '.25rem',
    },
    descriptionText: {
        color: '#212529',
    },
    descriptionBadge: {
        fontSize: '0.75rem',
        padding: '4.2px 7.8px',
        borderRadius: '0.375rem',
        color: '#FFFFFF',
        lineHeight: 1,
    },
    buttonGroup: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '24px',
        padding: '16px 20px',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        border: '1px solid #E5E7EB',
    },
    searchInputWrapper: {
        width: '100%',
        maxWidth: '340px',
        flex: '1 1 280px',
        [theme.breakpoints.down('xs')]: {
            maxWidth: '100%',
        },
    },
    searchField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#F9FAFB',
            height: 38,
            '& fieldset': {
                borderColor: '#E5E7EB',
            },
            '&:hover fieldset': {
                borderColor: '#D1D5DB',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#2563EB',
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 12px',
            fontSize: '0.875rem',
            color: '#374151',
        },
        '& .MuiOutlinedInput-input::placeholder': {
            color: '#9CA3AF',
            opacity: 1,
        },
    },
    titleSection: {
        color: '#111827',
        marginBottom: '1rem',
        fontSize: '1.5rem',
        fontWeight: '600',
    },
    captionSection: {
        fontSize: '0.875rem',
        color: '#6B7280',
        fontWeight: '500',
        marginTop: '0.25rem',
    },
    loadingText: {
        color: '#111827',
        textAlign: 'center',
        padding: '2rem',
        fontSize: '1.1rem',
        fontWeight: '500'
    }
});

class WorkshopHomePage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            HOME_Cotacoes: [],
            HOME_StatusAtual: null,
            contadores: createDefaultCounters(),
            dadosFiltrados: [],
            loading: true,
            lastUpdate: new Date(),
            clientsModalOpen: false,
            searchTerm: '',
            quickCompanyModalOpen: false,
            loggedUser: null
        };

        this.intervalId = null;
    }

    componentDidMount() {
        this.initHome();
        this.intervalId = setInterval(this.atualizarHome, 30000);
    }

    componentWillUnmount() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
        this.cleanupHome();
    }

    initHome = async () => {
        try {
            this.setState({ loading: true });

            const [data, loggedUser] = await Promise.all([
                WorkshopHomeService.getDashboardData(),
                LoggedUserService.getLoggedUser()
            ]);
            const firstStatus = data.HOME_Cotacoes.length > 0 ? normalizeStatus(data.HOME_Cotacoes[0].status) : null;

            this.setState({
                HOME_Cotacoes: data.HOME_Cotacoes,
                contadores: { ...createDefaultCounters(), ...data.contadores },
                loading: false,
                lastUpdate: new Date(),
                loggedUser: loggedUser
            }, () => {
                if (firstStatus) {
                    this.setState({ HOME_StatusAtual: firstStatus });
                    this.gerarRelatorioPorStatus(firstStatus);
                }
            });

        } catch (error) {
            console.error('Erro ao carregar dados da Home:', error);
            this.setState({ loading: false });
            Swal.fire(this.props.currentLanguage.bepiService.shared.shared_details_error_label, this.props.currentLanguage.bepiService.workshop.workshop_home_load_error, 'error');
        }
    }

    cleanupHome = () => {
        this.setState({
            HOME_Cotacoes: [],
            HOME_StatusAtual: null,
            dadosFiltrados: [],
            contadores: createDefaultCounters(),
        });
    }

    filtrarStatus = async (status) => {
        const currentStatus = normalizeStatus(status);

        try {
            this.setState({ loading: true });
            const dadosFiltrados = await WorkshopHomeService.getDataByStatus(currentStatus);

            this.setState({
                HOME_StatusAtual: currentStatus,
                dadosFiltrados,
                loading: false
            });
        } catch (error) {
            console.error('Erro ao filtrar por status:', error);
            this.setState({ loading: false });
            this.gerarRelatorioPorStatus(currentStatus);
        }
    }

    gerarRelatorioPorStatus = (status) => {
        const currentStatus = normalizeStatus(status);
        const filtrados = this.state.HOME_Cotacoes.filter((cotacao) => normalizeStatus(cotacao.status) === currentStatus);
        this.setState({ dadosFiltrados: filtrados, HOME_StatusAtual: currentStatus });
    }

    handleSearchChange = (event) => {
        this.setState({ searchTerm: event.target.value });
    }

    isGlobalSearchActive = () => this.state.searchTerm.trim().length > 0

    getDisplayedRows = () => {
        const { HOME_Cotacoes, dadosFiltrados, searchTerm } = this.state;

        if (!this.isGlobalSearchActive()) {
            return dadosFiltrados;
        }

        return HOME_Cotacoes.filter((item) => matchesGlobalSearch(item, searchTerm));
    }

    hasNotaRemessaFieldAvailable = () =>
        this.state.HOME_Cotacoes.some((item) => item.hasNotaRemessaField)

    buscar_equipamento = async () => {
        const { currentLanguage } = this.props;
        const { value: equipamentoId, isConfirmed } = await Swal.fire({
            title: currentLanguage.bepiService.workshop.workshop_home_search_equip_title,
            input: 'text',
            inputLabel: currentLanguage.bepiService.workshop.workshop_home_search_equip_label,
            showCancelButton: true,
            confirmButtonText: currentLanguage.bepiService.workshop.workshop_home_search_btn,
            cancelButtonText: currentLanguage.bepiService.shared.shared_details_cancel_btn
        });

        if (!isConfirmed || !equipamentoId) return;

        try {
            const equipamento = await WorkshopHomeService.getEquipmentById(equipamentoId);

            if (equipamento && equipamento.id) {
                localStorage.setItem('add_equip_edit', equipamentoId);
                this.props.history.push('/bepi/workshop/equipament/add');
            } else {
                throw new Error('Equipamento n\u00e3o encontrado');
            }

        } catch (err) {
            console.error(err);
            Swal.fire(currentLanguage.bepiService.workshop.workshop_home_equip_not_found, '', 'warning');
        }
    }

    atualizarHome = async () => {
        try {
            const data = await WorkshopHomeService.refreshDashboard();

            this.setState({
                HOME_Cotacoes: data.HOME_Cotacoes,
                contadores: { ...createDefaultCounters(), ...data.contadores },
                lastUpdate: new Date()
            }, () => {
                if (this.state.HOME_StatusAtual) {
                    this.gerarRelatorioPorStatus(this.state.HOME_StatusAtual);
                }
            });

        } catch (err) {
            console.error('Falha no refresh:', err);
        }
    }

    handleCarregarOrcamento = (userId, cliente, id) => {
        this.props.history.push(`/bepi/workshop/quote`);
    }

    handleCarregarDetalhes = (tipo, id, cliente, userId) => {
        this.props.history.push(`/bepi/workshop/quote/details?id=${id}`);
    }

    getCounter = (status) => {
        const parsed = Number(this.state.contadores?.[status]);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    handleAssumirQuotation = async (item) => {
        try {
            const loggedUser = await LoggedUserService.getLoggedUser();
            if (!loggedUser || (!loggedUser.id && !loggedUser.user_id)) {
                Swal.fire('Erro', 'Usu\u00e1rio n\u00e3o logado.', 'error');
                return;
            }

            const result = await Swal.fire({
                title: 'Confirmar',
                text: 'Deseja assumir esta cota\u00e7\u00e3o?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sim',
                cancelButtonText: 'N\u00e3o'
            });

            if (result.isConfirmed) {
                this.setState({ loading: true });
                const response = await WorkshopHomeService.assumeQuotation(item.id, loggedUser);

                if (response.success) {
                    Swal.fire('Sucesso', 'Cota\u00e7\u00e3o assumida com sucesso.', 'success');

                    const updateItem = (c) => {
                        if (c.id === item.id) {
                            return {
                                ...c,
                                assumed_by_workshop_user_id: loggedUser.id || loggedUser.user_id,
                                assumed_by_workshop_user_name: loggedUser.name || loggedUser.nome || loggedUser.userName,
                                assumed_by_workshop_at: new Date().toISOString()
                            };
                        }
                        return c;
                    };

                    this.setState(prevState => ({
                        HOME_Cotacoes: prevState.HOME_Cotacoes.map(updateItem),
                        dadosFiltrados: prevState.dadosFiltrados.map(updateItem),
                    }));
                }
            }
        } catch (error) {
            console.error('Erro ao assumir:', error);
                Swal.fire('Erro', error?.message || 'Erro ao assumir cota\u00e7\u00e3o.', 'error');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleUnassignQuotation = async (item) => {
        try {
            const loggedUser = this.state.loggedUser || await LoggedUserService.getLoggedUser();
            if (!loggedUser || (!loggedUser.id && !loggedUser.user_id)) {
                Swal.fire('Erro', 'Usuário não logado.', 'error');
                return;
            }

            const loggedUserId = String(loggedUser.id || loggedUser.user_id);
            if (String(item.assumed_by_workshop_user_id) !== loggedUserId) {
                Swal.fire('Erro', 'Você só pode devolver cotações assumidas por você.', 'error');
                return;
            }

            const result = await Swal.fire({
                title: 'Confirmar',
                text: 'Deseja devolver esta cotação para a fila?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim, devolver',
                cancelButtonText: 'Não'
            });

            if (result.isConfirmed) {
                this.setState({ loading: true });
                const response = await WorkshopHomeService.unassignQuotation(item.id, loggedUser);

                if (response.success) {
                    Swal.fire('Sucesso', 'Cotação devolvida para a fila.', 'success');

                    const clearItem = (c) => {
                        if (c.id === item.id) {
                            return {
                                ...c,
                                assumed_by_workshop_user_id: null,
                                assumed_by_workshop_user_name: null,
                                assumed_by_workshop_at: null
                            };
                        }
                        return c;
                    };

                    this.setState(prevState => ({
                        HOME_Cotacoes: prevState.HOME_Cotacoes.map(clearItem),
                        dadosFiltrados: prevState.dadosFiltrados.map(clearItem),
                    }));
                }
            }
        } catch (error) {
            console.error('Erro ao devolver:', error);
            Swal.fire('Erro', error?.message || 'Erro ao devolver cotação.', 'error');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleMarcarComoEnviado = async (item) => {
        const { currentLanguage } = this.props;

        if (normalizeStatus(item.status) !== 'pronto para retirada') {
            return;
        }

        try {
            const result = await Swal.fire({
                title: currentLanguage.bepiService.workshop.workshop_home_mark_sent_confirm_title || 'Confirmar',
                text: currentLanguage.bepiService.workshop.workshop_home_mark_sent_confirm_text || 'Deseja avancar este item para Enviado?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sim',
                cancelButtonText: 'Nao'
            });

            if (!result.isConfirmed) {
                return;
            }

            this.setState({ loading: true });
            await WorkshopHomeService.updateEstimateStatus(item.id, 'enviado');
            await this.atualizarHome();

            Swal.fire(
                currentLanguage.bepiService.shared.shared_details_success_title || 'Sucesso',
                currentLanguage.bepiService.workshop.workshop_home_mark_sent_success || 'Status atualizado para Enviado.',
                'success'
            );
        } catch (error) {
            console.error('Erro ao atualizar status para enviado:', error);
            Swal.fire(
                currentLanguage.bepiService.shared.shared_details_error_label || 'Erro',
                error?.message || currentLanguage.bepiService.workshop.workshop_home_mark_sent_error || 'Nao foi possivel atualizar para Enviado.',
                'error'
            );
        } finally {
            this.setState({ loading: false });
        }
    }

    handleAvancarAutorizacaoManutencao = async (item) => {
        const { currentLanguage } = this.props;
        const currentStatus = normalizeStatus(item.status);

        if (currentStatus !== 'autorizar manutencao' && !currentStatus.includes('pendente')) {
            return;
        }

        const isAuthorizationStep = currentStatus.includes('pendente');
        const nextStatus = isAuthorizationStep ? 'autorizar manutencao' : 'aprovado para manutencao';
        const confirmTitle = isAuthorizationStep
            ? currentLanguage.bepiService.workshop.workshop_home_authorize_maintenance_confirm_title
            : currentLanguage.bepiService.workshop.workshop_home_approve_maintenance_confirm_title;
        const confirmText = isAuthorizationStep
            ? currentLanguage.bepiService.workshop.workshop_home_authorize_maintenance_confirm_text
            : currentLanguage.bepiService.workshop.workshop_home_approve_maintenance_confirm_text;
        const successText = isAuthorizationStep
            ? currentLanguage.bepiService.workshop.workshop_home_authorize_maintenance_success
            : currentLanguage.bepiService.workshop.workshop_home_approve_maintenance_success;
        const errorText = isAuthorizationStep
            ? currentLanguage.bepiService.workshop.workshop_home_authorize_maintenance_error
            : currentLanguage.bepiService.workshop.workshop_home_approve_maintenance_error;

        try {
            const result = await Swal.fire({
                title: confirmTitle || 'Confirmar',
                text: confirmText || 'Deseja avancar este item?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sim',
                cancelButtonText: 'Nao'
            });

            if (!result.isConfirmed) {
                return;
            }

            this.setState({ loading: true });
            await WorkshopHomeService.updateEstimateStatus(item.id, nextStatus);
            await this.atualizarHome();

            Swal.fire(
                currentLanguage.bepiService.shared.shared_details_success_title || 'Sucesso',
                successText || 'Status atualizado com sucesso.',
                'success'
            );
        } catch (error) {
            console.error('Erro ao avancar status de autorizacao de manutencao:', error);
            Swal.fire(
                currentLanguage.bepiService.shared.shared_details_error_label || 'Erro',
                error?.message || errorText || 'Nao foi possivel atualizar o status.',
                'error'
            );
        } finally {
            this.setState({ loading: false });
        }
    }

    renderStatusCards = () => {
        const { classes, currentLanguage } = this.props;

        const getColorForStatus = (status) => {
            const statusAdministrativo = ['aguardando estimativa', 'aguardando orçamento', 'aguardando faturamento'];
            const statusCliente = ['estimativa entregue', 'pendente de aprovação'];
            if (statusAdministrativo.includes(status)) return '#04476c'; // Admin: Azul
            if (statusCliente.includes(status)) return '#f8792a'; // Cliente: Laranja
            return '#046c2c'; // Oficina: Verde
        };

        const statusCards = [
            {
                status: 'aguardando equipamento',
                label: currentLanguage.bepiService.workshop.workshop_status_waiting_equip
            },
            {
                status: 'equipamento em analise',
                label: currentLanguage.bepiService.workshop.workshop_status_in_analysis
            },
            {
                status: 'pendente de aprova\u00e7\u00e3o',
                label: currentLanguage.bepiService.workshop.workshop_status_pending_approval
            },
            {
                status: 'autorizar manutencao',
                label: currentLanguage.bepiService.workshop.workshop_status_authorize_maintenance
            },
            {
                status: 'aprovado para manutencao',
                label: currentLanguage.bepiService.workshop.workshop_status_approved
            },
            {
                status: 'aguardando inspe\u00e7\u00e3o e laudo',
                label: currentLanguage.bepiService.workshop.workshop_status_waiting_inspection
            },
            {
                status: 'pronto para retirada',
                label: currentLanguage.bepiService.workshop.workshop_status_ready
            },
            {
                status: 'enviado',
                label: currentLanguage.bepiService.workshop.workshop_status_sent
            }
        ];

        return (
            <Box className={classes.cardsWrapper}>
                {statusCards.map((card) => {
                    const cor = getColorForStatus(card.status.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
                    return (
                        <Box key={card.status}>
                            <Box
                                className={classes.statusCard}
                                onClick={() => this.filtrarStatus(card.status)}
                                style={{ borderLeft: `8px solid ${cor}` }}
                            >
                                <Typography className={classes.statusCounter}>
                                    {this.getCounter(card.status)}
                                </Typography>
                                <Typography variant="body2" className={classes.statusBadge} style={{ backgroundColor: cor }}>
                                    {card.label}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    }

    renderTabela = () => {
        const { HOME_StatusAtual, loading, lastUpdate, searchTerm } = this.state;
        const { classes, currentLanguage } = this.props;
        const isGlobalSearchActive = this.isGlobalSearchActive();
        const displayedRows = this.getDisplayedRows();
        const statusHeaderLabel = currentLanguage.bepiService?.shared?.shared_quotation_table_status || 'Status';

        if (loading) {
            return (
                <Typography className={classes.loadingText}>
                    {currentLanguage.bepiService.workshop.workshop_home_loading_msg}
                </Typography>
            );
        }

        return (
            <Box>
                <Typography variant="h5" className={classes.titleSection}>
                    {isGlobalSearchActive
                        ? `${(currentLanguage.bepiService.workshop.workshop_home_search_btn_main || 'Pesquisar').toUpperCase()} (${searchTerm})`
                        : (HOME_StatusAtual ? HOME_StatusAtual.toUpperCase() : currentLanguage.bepiService.workshop.workshop_home_select_status)}
                    <br />
                    <Typography className={classes.captionSection}>
                        {currentLanguage.bepiService.workshop.workshop_home_last_update}: {lastUpdate.toLocaleTimeString(currentLanguage.lang_code === 'en' ? 'en-US' : 'pt-BR')}
                    </Typography>
                </Typography>

                <Paper className={classes.tableWrapper}>
                    <Table className={classes.tableTransparent} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{currentLanguage.bepiService.workshop.workshop_home_table_created}</TableCell>
                                <TableCell>{currentLanguage.bepiService.workshop.workshop_home_table_code}</TableCell>
                                <TableCell>{currentLanguage.bepiService.workshop.workshop_home_table_cnpj}</TableCell>
                                <TableCell>{currentLanguage.bepiService.workshop.workshop_home_table_client}</TableCell>
                                <TableCell align="center">{'T\u00e9cnico Resp.'}</TableCell>
                                <TableCell align="center">{'N\u00ba Nota Remessa'}</TableCell>
                                {isGlobalSearchActive && <TableCell>{statusHeaderLabel}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedRows.length > 0 ? (
                                displayedRows.map((item, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{WorkshopHomeService.formatarData(item.data)}</TableCell>
                                        <TableCell>
                                            <Typography
                                                className={classes.actionLink}
                                                onClick={() => {
                                                    if (item.tipo && item.tipo.toLowerCase() !== 'estimativa') {
                                                        this.props.history.push('/bepi/workshop/quote');
                                                    } else {
                                                        this.props.history.push(`/bepi/workshop/quote/details?id=${item.id}`);
                                                    }
                                                }}
                                            >
                                                {item.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                className={classes.actionLink}
                                                // onClick={() => this.props.history.push('/bepi/workshop/quotations')}
                                                style={{ cursor: 'default' }}
                                            >
                                                {item.cnpj}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{item.cliente}</TableCell>
                                        {isGlobalSearchActive && <TableCell>{item.status || '-'}</TableCell>}
                                        <TableCell align="center">
                                            {item.assumed_by_workshop_user_id ? (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                    <span style={{ fontSize: '0.85rem', color: '#ffffff', fontWeight: 500 }}>
                                                        {item.assumed_by_workshop_user_name}
                                                    </span>
                                                    {String(item.assumed_by_workshop_user_id) === String(this.state.loggedUser?.id || this.state.loggedUser?.user_id) && (
                                                        <CustomButton
                                                            variant="redOutlined"
                                                            size="small"
                                                            onClick={() => this.handleUnassignQuotation(item)}
                                                        >
                                                            Devolver
                                                        </CustomButton>
                                                    )}
                                                </div>
                                            ) : (
                                                <CustomButton
                                                    variant="darkBlueFilled"
                                                    size="small"
                                                    onClick={() => this.handleAssumirQuotation(item)}
                                                >
                                                    Assumir
                                                </CustomButton>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <span style={{ fontSize: '0.85rem', color: '#ffffff' }}>
                                                {item.notaRemessaFileName ? normalizeFileNameWithoutExtension(item.notaRemessaFileName) : '-'}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={isGlobalSearchActive ? 7 : 6} align="center" style={{ color: '#FFFFFF' }}>
                                        {currentLanguage.bepiService.workshop.workshop_home_no_data_msg}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            </Box>
        );
    }

    render() {
        const { classes, currentLanguage } = this.props;
        const { clientsModalOpen, searchTerm } = this.state;

        return (
            <WorkshopLayout excludeMargin currentLanguage={currentLanguage}>
                <WorkshopHelpButton view="home" />
                <Box className={classes.root}>
                    <Container>
                        <Box className={classes.buttonGroup}>
                            <CustomButton 
                                variant="greenFilled" 
                                onClick={() => this.setState({ quickCompanyModalOpen: true })}
                            >
                                Entrada de Equipamento (Empresa Rápida)
                            </CustomButton>

                            <Box className={classes.searchInputWrapper}>
                                <TextField
                                    placeholder={currentLanguage.bepiService.workshop.workshop_home_search_cnpj_nota_placeholder}
                                    value={searchTerm}
                                    onChange={this.handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    className={classes.searchField}
                                />
                            </Box>
                        </Box>

                        <Card className={classes.panelCard}>
                            <Typography className={classes.panelTitle}>
                                {currentLanguage.bepiService.workshop.workshop_home_operation_phases}
                            </Typography>
                            {this.renderStatusCards()}
                            {/*<Box className={classes.panelDescription}>*/}
                            {/*    <Typography className={classes.descriptionText}>*/}
                            {/*        {currentLanguage.bepiService.workshop.workshop_home_responsible_label}:*/}
                            {/*    </Typography>*/}
                            {/*    <Box component="span" className={classes.descriptionBadge} style={{ backgroundColor: '#04476c' }}>*/}
                            {/*     linha d   {currentLanguage.bepiService.workshop.workshop_home_resp_admin}*/}
                            {/*    </Box>*/}
                            {/*    <Box component="span" className={classes.descriptionBadge} style={{ backgroundColor: '#f8792a' }}>*/}
                            {/*        {currentLanguage.bepiService.workshop.workshop_home_resp_client}*/}
                            {/*    </Box>*/}
                            {/*    <Box component="span" className={classes.descriptionBadge} style={{ backgroundColor: '#046c2c' }}>*/}
                            {/*        {currentLanguage.bepiService.workshop.workshop_home_resp_workshop}*/}
                            {/*    </Box>*/}
                            {/*</Box>*/}
                        </Card>
                        {this.renderTabela()}
                    </Container>
                </Box>

                <ClientsModal
                    open={clientsModalOpen}
                    onClose={() => this.setState((prev) => ({ clientsModalOpen: !prev.clientsModalOpen }))}
                    currentLanguage={currentLanguage}
                />
                <QuickCompanyModal
                    mode="existing"
                    open={this.state.quickCompanyModalOpen}
                    onClose={() => this.setState({ quickCompanyModalOpen: false })}
                    onSuccess={() => this.atualizarHome()}
                    currentLanguage={currentLanguage}
                    enqueueSnackbar={this.props.enqueueSnackbar}
                />
            </WorkshopLayout>
        );
    }
}

export default withRouter(withSnackbar(withStyles(styles)(WorkshopHomePage)));